const MAX_SCALE = 4
// A pinch can shrink the photo a little for feel, then it springs back to fit.
const MIN_PINCH_SCALE = 0.75
const DOUBLE_TAP_SCALE = 2.5
// Releasing just above 1x snaps back to fit, so a near-miss pinch doesn't leave swiping blocked.
const SNAP_BACK_BELOW_SCALE = 1.05
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_DISTANCE_PX = 40
const TAP_MOVE_TOLERANCE_PX = 10
// How far past the photo's edge a drag goes before the carousel takes it, so a pan that just reaches the edge stays a pan.
const SWIPE_HANDOFF_PX = 10
const SETTLE_TRANSITION = 'transform 0.2s ease-out'

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const distanceBetween = (first, second) => Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY)
const midpointOf = (first, second) => ({ x: (first.clientX + second.clientX) / 2, y: (first.clientY + second.clientY) / 2 })

// When the photo is narrower than the slide it stays centred, otherwise its edges can't come inside the slide's.
const clampAxis = (offset, slideStart, slideEnd, halfSize) => {
  const min = slideEnd - halfSize
  const max = slideStart + halfSize
  return min > max ? (min + max) / 2 : clamp(offset, min, max)
}

// Pinch and double tap to zoom a photo, and drag with one finger to look around it while zoomed.
// The page's own pinch zoom can't be used: the fullscreen scroll lock cancels every touchmove on iOS.
// Dragging on past the photo's edge hands the touch to the carousel, as in iOS Photos. Pass vertical for a vertical carousel.
// Returns a function that removes it again and eases the photo back to fit.
export const attachPinchZoom = (element, { vertical = false } = {}) => {
  let scale = 1
  let x = 0
  let y = 0
  let gesture = null
  let tap = null
  let lastTap = null

  const render = (animate) => {
    element.style.transition = animate ? SETTLE_TRANSITION : ''
    element.style.transform = scale === 1 && x === 0 && y === 0 ? '' : `translate3d(${x}px, ${y}px, 0) scale(${scale})`
  }

  // Scaling is about the photo's own centre, so this is its centre with our translate taken back off.
  const measure = () => {
    const rect = element.getBoundingClientRect()
    return {
      centre: { x: rect.left + rect.width / 2 - x, y: rect.top + rect.height / 2 - y },
      slide: element.parentElement.getBoundingClientRect()
    }
  }

  const clampToSlide = ({ centre, slide }) => {
    x = clampAxis(x, slide.left - centre.x, slide.right - centre.x, (element.offsetWidth * scale) / 2)
    y = clampAxis(y, slide.top - centre.y, slide.bottom - centre.y, (element.offsetHeight * scale) / 2)
  }

  const beginPinch = (touches) => {
    const [first, second] = touches
    gesture = {
      type: 'PINCH',
      ...measure(),
      startDistance: Math.max(distanceBetween(first, second), 1),
      startMidpoint: midpointOf(first, second),
      startScale: scale,
      startX: x,
      startY: y
    }
    tap = null
  }

  const beginPan = (touch) => {
    gesture = { type: 'PAN', ...measure(), startTouch: { x: touch.clientX, y: touch.clientY }, startX: x, startY: y }
  }

  const pinchTo = (touches) => {
    const [first, second] = touches
    const { centre, startDistance, startMidpoint, startScale, startX, startY } = gesture
    const nextScale = clamp((startScale * distanceBetween(first, second)) / startDistance, MIN_PINCH_SCALE, MAX_SCALE)
    const midpoint = midpointOf(first, second)
    // Keeps the part of the photo that started between the fingers between them as they spread and move.
    x = midpoint.x - centre.x - (nextScale * (startMidpoint.x - centre.x - startX)) / startScale
    y = midpoint.y - centre.y - (nextScale * (startMidpoint.y - centre.y - startY)) / startScale
    scale = nextScale
  }

  const panTo = (touch) => {
    const moved = { x: touch.clientX - gesture.startTouch.x, y: touch.clientY - gesture.startTouch.y }
    x = gesture.startX + moved.x
    y = gesture.startY + moved.y
    const wanted = vertical ? y : x
    clampToSlide(gesture)
    const overshoot = Math.abs(wanted - (vertical ? y : x))
    // Mostly along the carousel is keen-slider's own test, and it drops the drag if this hands over one that fails it.
    const isAlongCarousel = vertical ? Math.abs(moved.y) > Math.abs(moved.x) : Math.abs(moved.x) > Math.abs(moved.y)
    if (overshoot > SWIPE_HANDOFF_PX && isAlongCarousel) {
      gesture.type = 'SWIPE'
    }
  }

  const settle = (bounds) => {
    if (scale < SNAP_BACK_BELOW_SCALE) {
      scale = 1
      x = 0
      y = 0
    } else {
      clampToSlide(bounds)
    }
    render(true)
  }

  const toggleZoomAt = (point) => {
    const bounds = measure()
    if (scale > 1) {
      scale = 1
      x = 0
      y = 0
    } else {
      scale = DOUBLE_TAP_SCALE
      x = (1 - scale) * (point.x - bounds.centre.x)
      y = (1 - scale) * (point.y - bounds.centre.y)
      clampToSlide(bounds)
    }
    render(true)
  }

  const handleTap = (point) => {
    const isDoubleTap =
      lastTap &&
      point.time - lastTap.time < DOUBLE_TAP_MS &&
      Math.hypot(point.x - lastTap.x, point.y - lastTap.y) < DOUBLE_TAP_DISTANCE_PX
    lastTap = isDoubleTap ? null : point
    if (isDoubleTap) {
      toggleZoomAt(point)
    }
  }

  const onTouchStart = (event) => {
    if (gesture?.type === 'SWIPE') {
      return
    }
    // Lands an in-flight settle animation, so measuring sees where the photo is headed rather than where it is mid-way.
    render(false)
    if (event.touches.length >= 2) {
      return beginPinch(event.touches)
    }
    const touch = event.touches[0]
    tap = { x: touch.clientX, y: touch.clientY, time: Date.now() }
    if (scale > 1) {
      beginPan(touch)
    }
  }

  const onTouchMove = (event) => {
    // Once handed over, the rest of the touch is the carousel's. keen-slider picks up from wherever the finger is now,
    // because it hasn't seen a touchmove since touchstart and it measures from the first one it sees.
    if (gesture?.type === 'SWIPE') {
      return
    }
    // The second finger can land on an overlay such as the title, so its touchstart never reaches the photo.
    if (event.touches.length >= 2 && gesture?.type !== 'PINCH') {
      beginPinch(event.touches)
    }
    const touch = event.touches[0]
    if (tap && Math.hypot(touch.clientX - tap.x, touch.clientY - tap.y) > TAP_MOVE_TOLERANCE_PX) {
      tap = null
    }
    if (!gesture) {
      return
    }
    // Keeps the slider from swiping away and the page from zooming around the photo.
    event.preventDefault()
    event.stopPropagation()
    if (gesture.type === 'PINCH' && event.touches.length >= 2) {
      pinchTo(event.touches)
    } else if (gesture.type === 'PAN') {
      panTo(touch)
    }
    render(false)
  }

  // Never stops propagation: keen-slider only ends a drag on its own finger's touchend, and if it misses that
  // it stays mid-drag and ignores every later swipe.
  const onTouchEnd = (event) => {
    if (event.touches.length > 0) {
      if (gesture && gesture.type !== 'SWIPE') {
        beginPan(event.touches[0])
      }
      return
    }
    if (gesture) {
      settle(gesture)
      gesture = null
    }
    if (tap && event.type === 'touchend') {
      handleTap(tap)
    }
    tap = null
  }

  const preventPageZoom = (event) => event.preventDefault()

  element.style.touchAction = 'none'
  element.addEventListener('touchstart', onTouchStart, { passive: true })
  element.addEventListener('touchmove', onTouchMove, { passive: false })
  element.addEventListener('touchend', onTouchEnd)
  element.addEventListener('touchcancel', onTouchEnd)
  element.addEventListener('gesturestart', preventPageZoom, { passive: false })

  return () => {
    element.removeEventListener('touchstart', onTouchStart)
    element.removeEventListener('touchmove', onTouchMove)
    element.removeEventListener('touchend', onTouchEnd)
    element.removeEventListener('touchcancel', onTouchEnd)
    element.removeEventListener('gesturestart', preventPageZoom)
    element.style.touchAction = ''
    if (!element.style.transform) {
      element.style.transition = ''
      return
    }
    // This runs as the photo is swiped away, where a jump back to fit would show. The image's own CSS transitions
    // come back once the ease is done.
    scale = 1
    x = 0
    y = 0
    render(true)
    element.addEventListener('transitionend', () => (element.style.transition = ''), { once: true })
  }
}
